/**
 * @file OrderFeedTab.tsx
 * @description Вкладка «Лента» карточки заказа (phase19.31, uiux-design.md
 * §4.1; контракт cabinet-api-contract.md §9).
 *
 * Только события ЭТОГО заказа одним списком: стадии, оплаты, отгрузки,
 * уведомления клиенту, заметки — по дням, новые сверху. Строки строит
 * реестр `feed-event-meta.ts` из полей записи (без служебных кодов и
 * «mock»), раскладывает общий `FeedList`. Фильтры — пилюли со счётчиками
 * сервера: «Все», «Стадии», «Уведомления», «Заметки».
 *
 * Вкладка остаётся смонтированной, а при возврате на неё тихо дочитывает
 * свежие события (`useOrderTabRevisit`). «+ Заметки» нет: создание записи
 * журнала с привязкой к заказу сервер пока не принимает (см. отчёт фазы).
 * Внизу — переход ко всей истории клиента, если есть право на клиентов.
 */

import { ArrowRight, History, SearchX } from "lucide-react";
import { useMemo } from "react";
import { Link, useParams } from "react-router";

import { useHasPermission } from "../../auth/hooks";
import { describeOrderFeedItem } from "../feed/feed-event-meta";
import { FeedList } from "../feed/FeedList";
import { FilterChips, type FilterChip } from "../ui/FilterChips";
import { ORDER_FEED_PAGE, useOrderFeed } from "../../hooks/useOrderFeed";
import { useOrderTabRevisit } from "../../hooks/useOrderTabRevisit";
import type { OrderDto, OrderFeedFilter } from "../../lib/api-orders";

/** Пусто под фильтром — своя фраза на каждый чип. */
const FILTERED_EMPTY: Record<OrderFeedFilter, string> = {
  all: "По заказу ещё нет событий",
  stages: "Смен стадий по этому заказу ещё не было.",
  notifications: "Уведомлений клиенту по этому заказу не было.",
  notes: "Заметок по этому заказу нет.",
};

interface Props {
  order: Pick<OrderDto, "id" | "customerId" | "customerDisplayName">;
  /** Вкладка открыта — при возвращении на неё лента перечитывается тихо. */
  active: boolean;
}

export function OrderFeedTab({ order, active }: Props) {
  const { slug } = useParams<{ slug: string }>();
  const canViewCustomers = useHasPermission("SHOP_VIEW_CUSTOMERS");
  const feed = useOrderFeed(order.id);
  useOrderTabRevisit(active, feed.reload);

  const entries = useMemo(() => feed.items.map(describeOrderFeedItem), [feed.items]);
  const c = feed.counters;
  const chips: FilterChip<OrderFeedFilter>[] = [
    { value: "all", label: "Все", count: c?.all },
    { value: "stages", label: "Стадии", count: c?.stages },
    { value: "notifications", label: "Уведомления", count: c?.notifications },
    { value: "notes", label: "Заметки", count: c?.notes },
  ];
  const filtered = feed.filter !== "all";

  const footer =
    canViewCustomers && order.customerId ? (
      <Link
        to={`/shop/${slug}/customers/${order.customerId}`}
        className="type-body inline-flex min-h-[var(--control-compact-h,3rem)] items-center gap-1 self-start rounded-md px-1 font-medium text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
      >
        Вся история клиента{order.customerDisplayName ? ` ${order.customerDisplayName}` : ""}
        <ArrowRight aria-hidden className="size-4" />
      </Link>
    ) : null;

  return (
    <FeedList
      aria-label="Лента заказа"
      entries={entries}
      loading={feed.loading}
      error={feed.error}
      onRetry={feed.reload}
      hasMore={feed.hasMore}
      loadingMore={feed.loadingMore}
      onLoadMore={feed.loadMore}
      moreLabel={`Показать ещё ${ORDER_FEED_PAGE}`}
      toolbar={<FilterChips items={chips} value={feed.filter} onChange={feed.setFilter} aria-label="Отбор событий ленты" />}
      empty={{
        icon: History,
        title: "По заказу ещё нет событий",
        description: "Здесь появятся смены стадий, уведомления клиенту и заметки.",
      }}
      filtered={filtered}
      filteredEmpty={{
        icon: SearchX,
        title: FILTERED_EMPTY[feed.filter],
        onReset: () => feed.setFilter("all"),
        resetLabel: "Показать все события",
      }}
      footer={footer}
    />
  );
}
